import { orderBy, where } from "firebase/firestore";
import React, { useMemo, useRef } from "react";
import {
    Accordion,
    Alert,
    Badge,
    Button,
    ButtonGroup,
    Card,
    Dropdown,
    ListGroup,
    Modal,
} from "react-bootstrap";
import {
    FaClock,
    FaCode,
    FaCog,
    FaCopy,
    FaDownload,
    FaEdit,
    FaFlask,
    FaGlobe,
    FaMapMarkerAlt,
    FaPlay,
    FaTrash,
} from "react-icons/fa";
import { TbSettingsExclamation } from "react-icons/tb";
import {
    Link,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router";

import { getFirebaseConsoleUrl } from "../../api/firestoreBaseApi.jsx";
import {
    TaskCollection,
    TaskCollectionGroup,
    TaskDocument,
    useAppCollection,
    useAreasDocument,
    useLocationDocument,
    useTaskDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import AppSettingsTree from "../../components/AppSettings/AppSettingsTree.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import {
    canEditTaskWithCommands,
    commandInfo,
} from "../../components/Commands/index.jsx";
import DevMenuButton from "../../components/DevMenuButton";
import DroneSelector from "../../components/DroneSelector";
import FileExporter from "../../components/FileExporter";
import FirestoreDevMenuItem from "../../components/FirestoreDevMenuItem";
import Layout, { Title } from "../../components/Layout/Layout";
import { useUser } from "../../contexts/user_provider";
import { getFirebaseFunction } from "../../corvusFirebase";
import urls from "../../urls.jsx";
import { deepMerge } from "../../utils/objectUtils.js";

function TaskDevMenuButton({
    className,
    viewData,
    setViewData,
    organizationId,
    locationId,
    taskId,
    task,
}) {
    return (
        <DevMenuButton className={className}>
            <Dropdown.Item onClick={() => setViewData(!viewData)}>
                <FaCode className="me-2" style={{ marginTop: "-2px" }} />
                {!viewData && <>View Data</>}
                {viewData && <>Hide Data</>}
            </Dropdown.Item>
            <Dropdown.Divider />
            <FileExporter name={`task_${taskId}`} data={task}>
                <FaDownload
                    className="me-2 text-success"
                    style={{
                        marginTop: "-2px",
                    }}
                />
                Export
            </FileExporter>
            <Dropdown.Divider />
            <FirestoreDevMenuItem
                as={Dropdown.Item}
                path={`organizations/${organizationId}/locations/${locationId}/tasks/${taskId}`}
            />
        </DevMenuButton>
    );
}

function TaskAppListItem({
    appName,
    isInstalled,
    isValid,
    isLoadingApps,
    hasCustomSettings,
    showSettingsIcon,
    onSettings,
}) {
    return (
        <div className="d-flex justify-content-between align-items-center w-100 pe-3">
            <div>
                {appName}

                {!isInstalled && isValid && !isLoadingApps && (
                    <Badge bg="warning" className="ms-1" size="sm">
                        Not installed
                    </Badge>
                )}

                {!isValid && !isLoadingApps && (
                    <Badge bg="danger" className="ms-1" size="sm">
                        Invalid
                    </Badge>
                )}
            </div>

            <div>
                {hasCustomSettings && (
                    <TbSettingsExclamation
                        className="me-2"
                        size="22"
                        style={{ marginTop: "-2px" }}
                    />
                )}
                {showSettingsIcon && (
                    <Button
                        as="span"
                        // role="button"
                        tabIndex={0}
                        variant="outline-secondary"
                        className="py-0 px-1 ms-auto"
                        onClick={onSettings}
                    >
                        <FaCog style={{ marginTop: "-2px" }} />
                    </Button>
                )}
            </div>
        </div>
    );
}

export function CommandPreview({ command, commandIdx, className, isValid }) {
    const PreviewComponent = commandInfo[command.type]?.Preview;

    return (
        <ListGroup.Item
            key={commandIdx}
            className={`d-flex justify-content-between align-items-start ${className}`}
        >
            <span>
                <b>{commandIdx + 1}.</b>
                {PreviewComponent && (
                    <>
                        <PreviewComponent
                            command={command}
                            commandIdx={commandIdx}
                        />
                        {isValid === false && (
                            <Badge bg="danger" className="ms-1" size="sm">
                                Invalid
                            </Badge>
                        )}
                    </>
                )}
            </span>
        </ListGroup.Item>
    );
}

export function isTaskCommandValid(command, areasDoc) {
    if (!areasDoc) return true;

    const columnKeys = Object.keys(areasDoc.areas);

    if (command.type === "SCHEDULE_MOVE_KAP") {
        return (
            columnKeys.includes(command?.settings?.startKap) &&
            columnKeys.includes(command?.settings?.endKap) &&
            Object.keys(areasDoc.areas[command?.settings?.startKap]).includes(
                command?.settings?.startLeg,
            ) &&
            Object.keys(areasDoc.areas[command?.settings?.endKap]).includes(
                command?.settings?.endLeg,
            )
        );
    }

    return true;
}

export default function TaskPage() {
    const { organizationId, locationId, taskId } = useParams();

    const [allApps, isLoadingAppDoc] = useAppCollection([
        orderBy("name", "asc"),
    ]);
    const [locationDoc, isLoadingLocationDoc] = useLocationDocument(
        organizationId,
        locationId,
    );
    const isLoadingApps = isLoadingAppDoc || isLoadingLocationDoc;

    const installedApps = useMemo(() => {
        const locationAppIds = (locationDoc?.apps || []).map((a) => a.id);
        return allApps.filter((a) => locationAppIds.includes(a.id));
    }, [allApps, locationDoc?.apps]);

    const [searchParams, setSearchParams] = useSearchParams();
    const itemRefs = useRef(new Map());
    const selectedAppId = searchParams.get("appId");

    const [viewData, setViewData] = React.useState(false);
    const [task] = useTaskDocument(organizationId, locationId, taskId);
    const [showDeleteTaskModal, setShowDeleteTaskModal] = React.useState(false);
    const [showRouteplannerTestModal, setShowRouteplannerTestModal] =
        React.useState(false);
    const [selectedDroneId, setSelectedDroneId] = React.useState("none");
    const [isCreatingRouteplannerTest, setIsCreatingRouteplannerTest] =
        React.useState(false);
    const [routeplannerTestSuccess, setRouteplannerTestSuccess] =
        React.useState("");
    const [routeplannerTestError, setRouteplannerTestError] =
        React.useState("");
    const routeplannerTestSubmitting = React.useRef(false);
    const { getUserIsAdmin } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const message = location.state?.message;
    const [areasDoc] = useAreasDocument(organizationId, locationId);

    function handleCloseDeleteTaskModal() {
        setShowDeleteTaskModal(false);
    }

    function openRouteplannerTestModal() {
        setSelectedDroneId("none");
        setRouteplannerTestSuccess("");
        setRouteplannerTestError("");
        setShowRouteplannerTestModal(true);
    }

    function closeRouteplannerTestModal() {
        if (routeplannerTestSubmitting.current) return;
        setShowRouteplannerTestModal(false);
        setSelectedDroneId("none");
        setRouteplannerTestError("");
    }

    async function onCreateRouteplannerTest() {
        if (
            routeplannerTestSubmitting.current ||
            selectedDroneId === "none" ||
            !getUserIsAdmin()
        )
            return;

        routeplannerTestSubmitting.current = true;
        setIsCreatingRouteplannerTest(true);
        setRouteplannerTestSuccess("");
        setRouteplannerTestError("");

        try {
            const result = await getFirebaseFunction(
                "createRouteplannerTestCase",
                "europe-west2",
            )({
                organizationId,
                locationId,
                droneId: selectedDroneId,
                taskId,
            });
            if (result.data?.success !== true) {
                throw new Error("Could not create the routeplanner test.");
            }
            setRouteplannerTestSuccess(
                `Routeplanner test created: ${result.data.testFile}`,
            );
            setShowRouteplannerTestModal(false);
            setSelectedDroneId("none");
        } catch (error) {
            setRouteplannerTestError(
                error.message ||
                    "Could not create the routeplanner test. Please try again.",
            );
        } finally {
            routeplannerTestSubmitting.current = false;
            setIsCreatingRouteplannerTest(false);
        }
    }

    async function onDuplicateTask() {
        const taskCopy = {
            ...task,
            deleted: false,
            name: `${task.name} (copy)`,
        };
        delete taskCopy.id;
        const newTaskId = await TaskCollection(organizationId, locationId).add(
            taskCopy,
        );

        navigate(urls.task(organizationId, locationId, newTaskId));
    }

    function onDeleteTask() {
        TaskDocument(organizationId, locationId, taskId)
            .delete()
            .then(() => {
                navigate(urls.tasks(organizationId, locationId));
            });
    }

    React.useEffect(() => {
        if (
            (organizationId === undefined || locationId === undefined) &&
            taskId !== undefined
        ) {
            TaskCollectionGroup.list([where("id", "==", taskId)]).then(
                (tasks) => {
                    if (tasks.length > 0) {
                        navigate(
                            urls.task(
                                tasks[0].organizationId,
                                tasks[0].locationId,
                                taskId,
                            ),
                            { replace: true },
                        );
                    }
                },
            );
        }
    }, [organizationId, locationId, taskId, navigate]);

    // task.id always exists (I don't agree but not going to change that now)
    const taskExists = task && Object.keys(task).length > 1;

    const taskApps = useMemo(() => {
        return (task?.apps ?? []).map((app) => {
            const appDocument = allApps.find((option) => option.id === app.id);
            const locationApp = locationDoc?.apps?.find(
                (locationApp) => locationApp.id === app.id,
            );

            const appSettings = appDocument?.settings ?? {};
            const locationAppSettings = locationApp?.settings ?? {};
            const taskAppSettings = app?.settings ?? {};

            const resolvedAppSettings = deepMerge(
                appSettings,
                locationAppSettings,
                taskAppSettings,
            );

            return {
                id: app.id,
                name: appDocument?.name ?? "Unknown app",
                isInstalled: installedApps.some(
                    (installedApp) => installedApp.id === app.id,
                ),
                isValid: !!appDocument,
                resolvedAppSettings: resolvedAppSettings,
                appSettings: taskAppSettings,
                hasCustomAppSettings: Object.keys(taskAppSettings).length > 0,
            };
        });
    }, [task?.apps, allApps, locationDoc, installedApps]);

    const isPartiallyInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        const viewHeight =
            window.innerHeight || document.documentElement.clientHeight;

        return rect.top < viewHeight && rect.bottom > 0;
    };

    const registerScrollItemRef = (appId) => (el) => {
        if (!el) return;
        itemRefs.current.set(appId, el);

        if (appId === selectedAppId) {
            setTimeout(() => {
                if (!isPartiallyInViewport(el)) {
                    el.scrollIntoView({ block: "start" });
                }
            }, 400);
        }
    };

    function render() {
        return (
            <Layout>
                <title>{`Task ${task.name} | MyCorvus`}</title>
                <Breadcrumbs
                    path={breadcrumbPaths.task}
                    location={locationDoc}
                    task={task}
                />
                {taskExists ? (
                    <>
                        <Card className="mb-4">
                            <Title
                                backButtonUrl={urls.tasks(
                                    organizationId,
                                    locationId,
                                )}
                                title={task.name}
                            >
                                {getUserIsAdmin() && (
                                    <TaskDevMenuButton
                                        className="ms-1 mb-1"
                                        organizationId={organizationId}
                                        locationId={locationId}
                                        taskId={taskId}
                                        viewData={viewData}
                                        task={task}
                                        setViewData={setViewData}
                                    />
                                )}
                                {canEditTaskWithCommands(
                                    task?.commands,
                                    getUserIsAdmin(),
                                    organizationId,
                                ) && (
                                    <Button
                                        variant="outline-secondary"
                                        as={Link}
                                        to={urls.taskSettings(
                                            organizationId,
                                            locationId,
                                            taskId,
                                        )}
                                        className="ms-1 mb-1"
                                    >
                                        <FaEdit style={{ marginTop: "-2px" }} />
                                    </Button>
                                )}
                                <Dropdown
                                    as={ButtonGroup}
                                    className="ms-1 mb-1"
                                >
                                    <Button
                                        variant="primary"
                                        as={Link}
                                        to={urls.taskStart(
                                            organizationId,
                                            locationId,
                                            taskId,
                                        )}
                                    >
                                        <FaPlay
                                            className="me-2"
                                            style={{
                                                marginTop: "-2px",
                                            }}
                                        />
                                        Start
                                    </Button>
                                    <Dropdown.Toggle
                                        split
                                        variant="primary"
                                        id="dropdown-split-basic"
                                    />
                                    <Dropdown.Menu align="end">
                                        <Dropdown.Item
                                            onClick={onDuplicateTask}
                                        >
                                            <FaCopy
                                                className="me-2 text-primary"
                                                style={{
                                                    marginTop: "-2px",
                                                }}
                                            />
                                            Duplicate
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() =>
                                                navigate(
                                                    `${urls.routineCreate(organizationId, locationId)}?taskId=${taskId}`,
                                                )
                                            }
                                        >
                                            <FaClock
                                                className="me-2 text-primary"
                                                style={{ marginTop: "-2px" }}
                                            />
                                            Create routine
                                        </Dropdown.Item>
                                        {getUserIsAdmin() === true && (
                                            <Dropdown.Item
                                                onClick={
                                                    openRouteplannerTestModal
                                                }
                                            >
                                                <FaFlask
                                                    className="me-2 text-primary"
                                                    style={{
                                                        marginTop: "-2px",
                                                    }}
                                                />
                                                Create routeplanner test
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Item
                                            onClick={() =>
                                                setShowDeleteTaskModal(true)
                                            }
                                            className="text-danger"
                                        >
                                            <FaTrash
                                                className="me-2"
                                                style={{
                                                    marginTop: "-2px",
                                                }}
                                            />
                                            Delete
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Title>
                            {getUserIsAdmin() === true && (
                                <Modal
                                    show={showRouteplannerTestModal}
                                    onHide={closeRouteplannerTestModal}
                                    backdrop={
                                        isCreatingRouteplannerTest
                                            ? "static"
                                            : true
                                    }
                                    keyboard={!isCreatingRouteplannerTest}
                                >
                                    <Modal.Header
                                        closeButton={
                                            !isCreatingRouteplannerTest
                                        }
                                    >
                                        <Modal.Title>
                                            Create routeplanner test
                                        </Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <p>
                                            Capture this Task, Location, Drone
                                            configuration, live Drone position,
                                            and assigned Docks for routeplanner
                                            testing.
                                        </p>
                                        <fieldset
                                            disabled={
                                                isCreatingRouteplannerTest
                                            }
                                        >
                                            <legend className="form-label fs-6">
                                                Drone
                                            </legend>
                                            <DroneSelector
                                                value={selectedDroneId}
                                                onChange={setSelectedDroneId}
                                            />
                                        </fieldset>
                                        {routeplannerTestError && (
                                            <Alert
                                                variant="danger"
                                                className="mt-3 mb-0"
                                            >
                                                {routeplannerTestError}
                                            </Alert>
                                        )}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={closeRouteplannerTestModal}
                                            disabled={
                                                isCreatingRouteplannerTest
                                            }
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={onCreateRouteplannerTest}
                                            disabled={
                                                selectedDroneId === "none" ||
                                                isCreatingRouteplannerTest
                                            }
                                        >
                                            {isCreatingRouteplannerTest
                                                ? "Creating test..."
                                                : "Create routeplanner test"}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            )}
                            <Modal
                                show={showDeleteTaskModal}
                                onHide={handleCloseDeleteTaskModal}
                            >
                                <Modal.Header closeButton>
                                    <Modal.Title>Delete Task</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    Are you sure you want to delete task{" "}
                                    <b>{task.name}</b>?
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleCloseDeleteTaskModal}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => onDeleteTask()}
                                    >
                                        <FaTrash
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Delete
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                            {viewData && (
                                <Card.Body className="p-4 pt-0">
                                    <CodeBlock name="Task" code={task} />
                                </Card.Body>
                            )}
                        </Card>
                        {routeplannerTestSuccess && (
                            <Alert
                                variant="success"
                                onClose={() => setRouteplannerTestSuccess("")}
                                dismissible
                            >
                                {routeplannerTestSuccess}
                            </Alert>
                        )}
                        {message && <Alert variant="warning">{message}</Alert>}
                        <Card className="mb-4">
                            <Card.Header className="d-flex justify-content-between">
                                <div style={{ fontWeight: "bold" }}>
                                    Commands
                                </div>
                            </Card.Header>
                            <ListGroup variant="flush">
                                {task.commands.map((command, commandIdx) => (
                                    <CommandPreview
                                        // biome-ignore lint/suspicious/noArrayIndexKey: Task commands have no stable identifier and their position is part of their identity.
                                        key={commandIdx}
                                        command={command}
                                        commandIdx={commandIdx}
                                        className="px-3"
                                        isValid={isTaskCommandValid(
                                            command,
                                            areasDoc,
                                        )}
                                    />
                                ))}
                            </ListGroup>
                        </Card>
                        <Card className="mb-4">
                            <Card.Header className="d-flex justify-content-between">
                                <div style={{ fontWeight: "bold" }}>Apps</div>
                            </Card.Header>

                            {getUserIsAdmin() ? (
                                <Accordion
                                    activeKey={selectedAppId}
                                    onSelect={(key) => {
                                        if (key) {
                                            setSearchParams(
                                                { appId: key },
                                                { replace: true },
                                            );
                                        } else {
                                            setSearchParams(
                                                {},
                                                { replace: true },
                                            );
                                        }
                                    }}
                                >
                                    {taskApps.map((taskApp) => {
                                        return (
                                            <Accordion.Item
                                                eventKey={taskApp.id}
                                                key={taskApp.id}
                                                ref={registerScrollItemRef(
                                                    taskApp.id,
                                                )}
                                            >
                                                <Accordion.Header>
                                                    <TaskAppListItem
                                                        appName={taskApp.name}
                                                        appId={taskApp.id}
                                                        isInstalled={
                                                            taskApp.isInstalled
                                                        }
                                                        isValid={
                                                            taskApp.isValid
                                                        }
                                                        isLoadingApps={
                                                            isLoadingApps
                                                        }
                                                        hasCustomSettings={
                                                            taskApp.hasCustomAppSettings
                                                        }
                                                        showSettingsIcon={true}
                                                        onSettings={(e) => {
                                                            e.stopPropagation();
                                                            navigate(
                                                                urls.taskAppSettings(
                                                                    organizationId,
                                                                    locationId,
                                                                    taskId,
                                                                    taskApp.id,
                                                                ),
                                                            );
                                                        }}
                                                    />
                                                </Accordion.Header>

                                                <Accordion.Body>
                                                    <div className="d-flex gap-2 mb-3">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() =>
                                                                navigate(
                                                                    urls.allApps() +
                                                                        `?appId=${taskApp.id}`,
                                                                )
                                                            }
                                                        >
                                                            <FaGlobe
                                                                className="me-2"
                                                                style={{
                                                                    marginTop:
                                                                        "-2px",
                                                                }}
                                                            />
                                                            Global app settings
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() =>
                                                                navigate(
                                                                    urls.locationApps(
                                                                        organizationId,
                                                                        locationId,
                                                                    ) +
                                                                        `?appId=${taskApp.id}`,
                                                                )
                                                            }
                                                        >
                                                            <FaMapMarkerAlt
                                                                className="me-2"
                                                                style={{
                                                                    marginTop:
                                                                        "-2px",
                                                                }}
                                                            />
                                                            Location app
                                                            settings
                                                        </Button>
                                                    </div>
                                                    <AppSettingsTree
                                                        resolvedSettings={
                                                            taskApp.resolvedAppSettings
                                                        }
                                                        overrideSettings={
                                                            taskApp.appSettings
                                                        }
                                                        url={getFirebaseConsoleUrl(
                                                            TaskDocument(
                                                                organizationId,
                                                                locationId,
                                                                taskId,
                                                            ).path,
                                                        )}
                                                    />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        );
                                    })}
                                </Accordion>
                            ) : (
                                <ListGroup variant="flush">
                                    {taskApps.map((taskApp) => {
                                        return (
                                            <ListGroup.Item
                                                key={taskApp.id}
                                                className="px-3 d-flex justify-content-between align-items-start"
                                            >
                                                <TaskAppListItem
                                                    appName={taskApp.name}
                                                    appId={taskApp.id}
                                                    isInstalled={
                                                        taskApp.isInstalled
                                                    }
                                                    isValid={taskApp.isValid}
                                                    isLoadingApps={
                                                        isLoadingApps
                                                    }
                                                    hasCustomSettings={false}
                                                    showSettingsIcon={false}
                                                />
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            )}
                        </Card>
                    </>
                ) : (
                    <Card className="mb-4">
                        <Title
                            title={"Task does not exist"}
                            backButtonUrl={urls.tasks(
                                organizationId,
                                locationId,
                            )}
                        />
                    </Card>
                )}
            </Layout>
        );
    }

    return task ? render() : null;
}
